// @ts-check
import React, { useCallback, useMemo, useState, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import _ from 'lodash';
import queryString from 'query-string';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import createMutationRequest from 'utils/createMutationRequest';
import createQueryRequest from 'services/createQueryRequest';
import useAPI from 'services/useAPI';
import { useTranslation } from 'localization/useTranslation';
import Tl from 'localization/LocalizationKeys';
import SimpleTable, { SortDirection } from 'components/Common/SimpleTable/SimpleTable';
import useAlerts from 'utils/hooks/useAlerts';
import ContentPage from 'components/ContentView/ContentPage/ContentPage';

import { parseModelId } from '../utils/model';
import ConfirmDeleteModalAssets from './ConfirmDeleteModalAssets';
import { modelKeys, useModelsQuery } from '../queries';
import { downloadObject } from 'utils/load';
import UploadTextFileModal from 'components/Modals/UploadTextFileModal';

/**
 * @typedef {object} ModelType
 * @property {string} id
 * @property {string} displayName
 * @property {string} modelType
 * @property {boolean} draft
 */

/**
 * @type {import('components/Common/SimpleTable/SimpleTable').ColumnType<ModelType>[]}
 */
const columns = [
  {
    label: Tl.model.version,
    path: 'id',
    isSortable: true,
    searchItem: true
  },
  {
    label: Tl.common.name,
    path: 'displayName',
    isSortable: true,
    searchItem: true
  },
  {
    label: Tl.common.modeltype,
    path: 'modelType',
    isSortable: true,
    searchItem: true
  },
  {
    label: Tl.common.draft,
    path: 'draft',
    isSortable: true,
    searchItem: true,
    render: val => val ? 'true' : 'false'
  }
];

/**
 * @type {import('components/Common/SimpleTable/SimpleTable').SortByType<ModelType>}
 * @constant
 */
const DefaultSortBy = {
  path: 'displayName',
  direction: SortDirection.DESC
};

const AdminModelPageList = ({ breadcrumbs }) => {
  const history = useHistory();
  const [hasError] = useState(false);
  const api = useAPI();
  const T = useTranslation();
  const location = useLocation();
  const [confirmDeleteItem, setConfirmDeleteItem] = useState(null);
  const [uploadModel, setUploadModel] = useState(null);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [alerts, addSuccessAlert, addErrorAlert] = useAlerts();
  const queryClient = useQueryClient();

  const { models, isLoading } = useModelsQuery({ includeDrafts: true });

  const draftDeleteQuery = useMutation({
    mutationKey: modelKeys.model(confirmDeleteItem?.id),
    mutationFn: createMutationRequest(api.core.deleteModelDraftItem),
    onMutate: useCallback((model) => {
      const oldData = queryClient.getQueryData(modelKeys.modelDrafts());
      queryClient.setQueryData(modelKeys.modelDrafts(), old => {
        return _.reject(old, { id: model.id });
      });
      return { model, oldData };
    }, [queryClient]),
    onSuccess: useCallback(() => {
      addSuccessAlert(T.common.request.delete.success);
      setConfirmDeleteItem(null);
      queryClient.invalidateQueries(modelKeys.modelDrafts());
    }, [T.common.request.delete.success, addSuccessAlert, queryClient]),
    onError: useCallback((error, variables, context) => {
      console.error(error);
      console.error(variables);
      queryClient.setQueryData(modelKeys.models(), context.oldData);
      addErrorAlert(T.common.request.delete.error);
    }, [T.common.request.delete.error, addErrorAlert, queryClient])
  });

  const { mutate } = useMutation({
    mutationFn: createMutationRequest(api.core.updateModelDraft),
    onMutate: useCallback((model) => {
      return model;
    }, []),
    onSuccess: useCallback(() => {
      addSuccessAlert(T.common.save_model);
    }, [T.common.save_model, addSuccessAlert]),
    onError: useCallback((error) => {
      addErrorAlert(T.common.save_model_failed);
      console.error(error);
    }, [T.common.save_model_failed, addErrorAlert])
  });

  const existingModelQuery = useQuery({
    enabled: !_.isEmpty(uploadModel?.id ?? ''),
    queryKey: modelKeys.model(uploadModel?.id),
    queryFn: createQueryRequest(api.core.getModel)
  });

  const modelDeleteQuery = useMutation({
    mutationKey: modelKeys.model(confirmDeleteItem?.id),
    mutationFn: createMutationRequest(api.core.deleteModelItem),
    onMutate: useCallback((model) => {
      const oldData = queryClient.getQueryData(modelKeys.models());
      queryClient.setQueryData(modelKeys.models(), old => {
        return _.reject(old, { id: model.id });
      });
      return { model, oldData };
    }, [queryClient]),
    onSuccess: useCallback(() => {
      addSuccessAlert(T.common.request.delete.success);
      setConfirmDeleteItem(null);
      queryClient.invalidateQueries(modelKeys.models());
    }, [T.common.request.delete.success, addSuccessAlert, queryClient]),
    onError: useCallback((error, variables, context) => {
      console.error(error);
      console.error(variables);
      queryClient.setQueryData(modelKeys.models(), context.oldData);
      addErrorAlert(T.common.request.delete.error);
    }, [T.common.request.delete.error, addErrorAlert, queryClient])
  });

  const onConfirm = useCallback((/** @type {ModelType} */model) => {
    if (model.draft === true) {
      draftDeleteQuery.mutate(model);
    } else {
      modelDeleteQuery.mutate(model);
    }

  }, [draftDeleteQuery, modelDeleteQuery]);

  const onRowClick = useCallback((model) => {
    history.push({ pathname: `/admin/models/${model.id}`, search: queryString.stringify({ modelDraft: model.draft }) });
  }, [history]);


  const onDelete = useCallback((item) => {
    setConfirmDeleteItem(item);
  }, []);

  const onNewVersion = useCallback((/** @type {ModelType} */item) => {
    const { version } = parseModelId(item.id);
    const params = {
      modelDraft: true,
      oldModelId: item.id,
      newVersion: parseInt(version, 10) + 1
    };
    history.push({ pathname: '/admin/models/new-model', search: queryString.stringify(params) });
  }, [history]);


  const onMigrateToModel = useCallback((/** @type {ModelType} */item) => {
    history.push({ pathname: `models/${item.id}/migrate` });
  }, [history]);

  const cancelDelete = useCallback(() => setConfirmDeleteItem(null), []);

  const disableNewVersion = useCallback((/** @type {ModelType} */item) => {
    return (item.draft === true);
  }, []);

  const onConfirmUpload = useCallback((/** @type {string} */content) => {
    const data = JSON.parse(content);
    setUploadModel(data);
    setIsUploadModalOpen(false);
  }, []);

  const openUploadModal = useCallback(() => {
    setIsUploadModalOpen(true);
  }, []);

  const cancelUpload = useCallback(() => {
    setIsUploadModalOpen(false);
  }, []);

  const onExportModel = useCallback((/** @type {ModelType} */item) => {
    downloadObject(item, `${item.displayName}.json`);
  }, []);

  useEffect(() => {
    if (uploadModel != null && !existingModelQuery.isLoading) {
      if (!_.isEmpty(existingModelQuery?.data)) {
        addErrorAlert(T.model.import.idExists);
      } else {
        const model = { ...uploadModel, draft: true };
        setUploadModel(null);
        mutate(model);
      }
    }
  }, [uploadModel, T.model.import.idExists, addErrorAlert, mutate, existingModelQuery?.data, existingModelQuery.isLoading]);

  const actions = useMemo(/** @return {import('components/Common/SimpleTable/SimpleTable').ActionType<ModelType>[]} */() => [
    {
      title: T.common.delete,
      onClick: onDelete
    },
    {
      title: T.model.list.new.version,
      onClick: onNewVersion,
      isDisabled: disableNewVersion
    },
    {
      title: T.migrate.assets,
      onClick: onMigrateToModel
    },
    {
      title: T.model.export,
      onClick: onExportModel
    }
  ], [T.common.delete, T.migrate.assets, T.model.list.new.version, T.model.export, disableNewVersion, onDelete, onMigrateToModel, onNewVersion, onExportModel]);

  /** @type {import('components/Common/SimpleTable/SimpleTable').GlobalAction[]} */
  const globalActions = [
    {
      label: T.model.list.new.asset,
      onClick: () => {
        history.push({ pathname: `${location.pathname}/new-model`, search: queryString.stringify({ modelDraft: true }) });
      }
    },
    {
      label: T.model.list.import.asset,
      onClick: openUploadModal,
      isLoading: uploadModel != null && existingModelQuery.isLoading
    }
  ];

  return (
    <ContentPage title={T.sidebar.item.models} help={'admin/models/list'} breadcrumb={breadcrumbs}>
      { alerts }
      <SimpleTable
        actions={actions}
        isLoading={isLoading || _.isEmpty(models)}
        onRowClick={onRowClick}
        data={models}
        columns={columns}
        sortBy={DefaultSortBy}
        hasError={hasError}
        errorText={T.admin.model.list.error}
        globalActions={globalActions}
        compact
        usePaging
      />
      <ConfirmDeleteModalAssets
        isLoading={draftDeleteQuery.isLoading || modelDeleteQuery.isLoading}
        onConfirm={onConfirm}
        item={confirmDeleteItem}
        itemName={`${confirmDeleteItem?.displayName} (${confirmDeleteItem?.id})` ?? T.common.noname}
        onCancel={cancelDelete}
      />
      <UploadTextFileModal
        onConfirm={onConfirmUpload}
        isOpen={isUploadModalOpen}
        onCancel={cancelUpload}
        uploadText={T.model.upload.json.title}
        confirmText={T.common.save}
        cancelText={T.common.cancel}
      />
    </ContentPage>
  );
};

export default AdminModelPageList;
